/**
 * Optimizer Control Center — "Restore last snapshot" row action.
 * One-click undo: reverts the flow/rotation to the most recent snapshot
 * (the state captured right before the last Apply). Full history lives in
 * the optimizer drawer's Flow history panel.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useState, type FC } from 'react';
import { RotateCcw } from 'lucide-react';

import { Button, Checkbox } from '@/components/ui';
import type { OptimizerRow } from '@/features/optimizer-center/optimizer-center.types';
import { queueRestoreProviderOf } from '@/features/trackers/flow-optimizer';
import { formatDateTimeGb } from '@/lib/utils';

import { ConfirmActionDialog } from './ConfirmActionDialog';
import { useRestoreSnapshot } from './useRestoreSnapshot';

const SOURCE_LABELS: Record<string, string> = {
  auto_apply: 'before last optimizer apply',
  auto_standard: 'before last Standard edit',
  auto_weights: 'before last weight push',
  auto_rotation: 'before last queue rotation',
  manual: 'manual backup',
};

export const RestoreSnapshotAction: FC<{ row: OptimizerRow }> = ({ row }) => {
  const [open, setOpen] = useState(false);
  const [syncQueue, setSyncQueue] = useState(true);
  const canSyncQueue = queueRestoreProviderOf(row.provider) !== null;
  const { latest, isLoading, isRestoring, restoreLatest } = useRestoreSnapshot(
    row,
    open,
  );

  const noSnapshot = !isLoading && !latest;
  const summary = isLoading
    ? 'Loading the latest snapshot…'
    : latest
      ? `Revert "${row.flowName}" to the snapshot from ${formatDateTimeGb(
          latest.createdAt,
        )} (${SOURCE_LABELS[latest.source] ?? latest.source})? Current weights will be overwritten.`
      : 'No snapshot found for this flow yet — one is captured automatically before each apply.';
  const description = (
    <div className="space-y-3">
      <p>{summary}</p>
      {latest && canSyncQueue ? (
        <label className="flex cursor-pointer items-start gap-2 text-xs">
          <Checkbox
            checked={syncQueue}
            onCheckedChange={(v) => setSyncQueue(v === true)}
          />
          <span>
            Also sync the queue/bank to the restored flow (recommended).
            Elements the restore removes keep their test history — recorded as
            removed by this restore, never wiped.
          </span>
        </label>
      ) : null}
    </div>
  );

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        aria-label="Restore last snapshot"
        title="Restore last snapshot"
        onClick={() => setOpen(true)}
      >
        <RotateCcw className="h-4 w-4" />
      </Button>
      {open && (
        <ConfirmActionDialog
          title="Restore last snapshot"
          description={description}
          confirmLabel={noSnapshot ? 'OK' : 'Restore'}
          isBusy={isRestoring || isLoading}
          onConfirm={() => {
            if (latest) restoreLatest(canSyncQueue && syncQueue);
            setOpen(false);
          }}
          onCancel={() => setOpen(false)}
        />
      )}
    </>
  );
};
